import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, X, Trash2, Clock, Calendar, MapPin, Mail, Phone, Globe, Loader2, LogOut, Eye, EyeOff, Plus, Sparkles, Star, Search } from 'lucide-react';
import { useEventSearch } from '@/hooks/useEventSearch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_STORAGE_KEY = 'cp_admin_token';

const ADMIN_SEARCH_FIELDS = ['name', 'organizer', 'city', 'address', 'postal_code', 'department', 'description', 'disciplines', 'phone', 'email', 'contact_email'];

function AdminLogin({ onLogin }) {
  const [token, setToken] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr('');
    const cleaned = token.trim();
    if (!cleaned) {
      setErr('Mot de passe requis.');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { token: cleaned });
      if (r.data?.success) {
        localStorage.setItem(TOKEN_STORAGE_KEY, cleaned);
        onLogin(cleaned);
      } else {
        setErr('Mot de passe incorrect.');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setErr('Mot de passe incorrect.');
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network')) {
        setErr('Erreur réseau — vérifiez votre connexion puis réessayez.');
      } else {
        setErr('Connexion impossible. Réessayez dans un instant.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center px-4 font-['Manrope']">
      <form onSubmit={submit} autoComplete="off" className="w-full max-w-sm rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/10 p-8" data-testid="admin-login-form">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FFC107]/15 text-[#FFC107] mb-3">
            <Clock className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-black mb-1">Accès Administration</h1>
          <p className="text-xs text-[#888]">Modération des ball-traps de campagne</p>
        </div>

        {/* Hidden dummy field to disable aggressive browser autofill */}
        <input type="text" name="fake-user" autoComplete="username" style={{ display: 'none' }} />

        <div className="relative mb-3">
          <input
            type={showPwd ? 'text' : 'password'}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Mot de passe administrateur"
            autoFocus
            autoComplete="new-password"
            spellCheck={false}
            name="cp-admin-pw"
            className="w-full px-4 py-3 pr-11 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
            data-testid="admin-token-input"
          />
          <button
            type="button"
            onClick={() => setShowPwd((s) => !s)}
            className="absolute top-1/2 right-3 -translate-y-1/2 p-1 text-white/50 hover:text-white"
            aria-label={showPwd ? 'Masquer' : 'Afficher'}
            data-testid="admin-toggle-show-pwd"
          >
            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {err && (
          <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs" data-testid="admin-login-error">
            <p className="font-semibold">{err}</p>
            {err === 'Mot de passe incorrect.' && (
              <p className="mt-1 text-red-300/80 font-normal">
                Si votre navigateur a pré-rempli le champ, effacez-le complètement et saisissez le mot de passe à la main.
              </p>
            )}
          </div>
        )}
        <button
          type="submit"
          disabled={loading || !token.trim()}
          className="w-full py-3 rounded-lg bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold disabled:opacity-60"
          data-testid="admin-login-btn"
        >
          {loading ? 'Vérification…' : 'Se connecter'}
        </button>
      </form>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
    approved: 'bg-green-500/15 text-green-300 border-green-500/30',
    rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
    cancelled: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  };
  const labels = { pending: 'En attente', approved: 'Validé', rejected: 'Refusé', cancelled: 'Annulé' };
  return (
    <span className={`inline-block px-2.5 py-0.5 text-[11px] rounded-full border font-semibold ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
}

function EventRow({ evt, onApprove, onReject, onDelete, onFeature, onUnfeature, busy }) {
  const posterSrc = evt.poster_url ? `${API.replace('/api','')}${evt.poster_url}` : null;
  const isPdf = (evt.poster_mime || '').includes('pdf') || (evt.poster_url || '').toLowerCase().endsWith('.pdf');
  const today = new Date().toISOString().slice(0, 10);
  const isFeatured = !!evt.featured && (!evt.featured_until || evt.featured_until >= today);

  return (
    <div
      className={`rounded-xl bg-white/[0.02] p-4 flex gap-4 border ${isFeatured ? 'border-[#FFC107]/50 shadow-[0_0_20px_-12px_rgba(255,193,7,0.4)]' : 'border-white/5'}`}
      data-testid={`admin-event-${evt.id}`}
    >
      {posterSrc && !isPdf && (
        <a href={posterSrc} target="_blank" rel="noreferrer" className="shrink-0">
          <img src={posterSrc} alt="Affiche" className="w-20 h-28 object-cover rounded-lg bg-black/40" />
        </a>
      )}
      {posterSrc && isPdf && (
        <a href={posterSrc} target="_blank" rel="noreferrer" className="shrink-0 w-20 h-28 flex flex-col items-center justify-center rounded-lg bg-[#FFC107]/10 text-[#FFC107] text-[10px] border border-[#FFC107]/30">
          <span>PDF</span>
        </a>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-white truncate">{evt.name}</h3>
            {isFeatured && (
              <span
                className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] rounded-full bg-[#FFC107]/20 text-[#FFC107] border border-[#FFC107]/40 font-bold uppercase tracking-wider"
                data-testid={`admin-featured-badge-${evt.id}`}
                title={evt.featured_source === 'admin_free' ? 'Mise en avant offerte par admin' : 'Mise en avant payée'}
              >
                <Sparkles className="w-3 h-3" /> À la une
                {evt.featured_source === 'admin_free' && <span className="ml-0.5 opacity-80">· gratuit</span>}
              </span>
            )}
          </div>
          <StatusBadge status={evt.status} />
        </div>
        {evt.organizer && <p className="text-xs text-[#FFC107]/90 mb-1">{evt.organizer}</p>}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#C8C8C8]">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{evt.date_start || '—'}{evt.date_end && ` → ${evt.date_end}`}</span>
          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[evt.city, evt.postal_code, evt.department && `(${evt.department})`].filter(Boolean).join(' ')}</span>
          {evt.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{evt.phone}</span>}
          {evt.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{evt.email}</span>}
          {evt.website && <a href={evt.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#FFC107] hover:underline"><Globe className="w-3 h-3" />Site</a>}
        </div>
        {(evt.disciplines || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {evt.disciplines.map((d) => (
              <span key={d} className="px-1.5 py-0.5 text-[10px] rounded bg-white/5 text-[#C8C8C8]">{d}</span>
            ))}
          </div>
        )}
        {evt.price_info && <p className="mt-1.5 text-xs text-white/80">💰 {evt.price_info}</p>}
        {evt.description && <p className="mt-1.5 text-xs text-[#888] line-clamp-2">{evt.description}</p>}
        {evt.contact_email && <p className="mt-1.5 text-[11px] text-[#666]">Soumis par : {evt.contact_email}</p>}

        <div className="flex flex-wrap gap-2 mt-3">
          {evt.status !== 'approved' && (
            <button onClick={() => onApprove(evt.id)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-green-500/15 hover:bg-green-500/25 text-green-200 border border-green-500/30 disabled:opacity-50" data-testid={`approve-btn-${evt.id}`}>
              <Check className="w-3 h-3" /> Valider
            </button>
          )}
          {evt.status !== 'rejected' && (
            <button onClick={() => onReject(evt.id)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/20 disabled:opacity-50" data-testid={`reject-btn-${evt.id}`}>
              <X className="w-3 h-3" /> Refuser
            </button>
          )}
          {evt.status === 'approved' && !isFeatured && (
            <button onClick={() => onFeature(evt.id)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[#FFC107]/15 hover:bg-[#FFC107]/25 text-[#FFC107] border border-[#FFC107]/30 disabled:opacity-50" data-testid={`feature-btn-${evt.id}`} title="Mettre en avant gratuitement (admin)">
              <Star className="w-3 h-3" /> Mettre en avant
            </button>
          )}
          {isFeatured && (
            <button onClick={() => onUnfeature(evt.id)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 disabled:opacity-50" data-testid={`unfeature-btn-${evt.id}`}>
              <X className="w-3 h-3" /> Retirer mise en avant
            </button>
          )}
          <button onClick={() => onDelete(evt.id)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 disabled:opacity-50 ml-auto" data-testid={`delete-btn-${evt.id}`}>
            <Trash2 className="w-3 h-3" /> Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCountryEvents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY) || '');
  const [tokenValidated, setTokenValidated] = useState(false);
  const [validating, setValidating] = useState(false);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState(searchParams.get('created') === '1' ? 'approved' : 'pending');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [createdToast, setCreatedToast] = useState(searchParams.get('created') === '1');

  // Auto-dismiss the success toast and clean the query param.
  useEffect(() => {
    if (!createdToast) return;
    const t = setTimeout(() => {
      setCreatedToast(false);
      setSearchParams({}, { replace: true });
    }, 4000);
    return () => clearTimeout(t);
  }, [createdToast, setSearchParams]);

  // SECURITY: before showing the dashboard shell at all, re-validate the
  // stored token server-side. Prevents "localStorage hack" where an attacker
  // could set a fake token and see the chrome (even though no data loads).
  useEffect(() => {
    if (!token) { setTokenValidated(false); return; }
    let cancelled = false;
    setValidating(true);
    axios.post(`${API}/admin/login`, { token })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.success) {
          setTokenValidated(true);
        } else {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          setToken('');
          setTokenValidated(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken('');
        setTokenValidated(false);
      })
      .finally(() => { if (!cancelled) setValidating(false); });
    return () => { cancelled = true; };
  }, [token]);

  const fetchEvents = useCallback(async () => {
    if (!token || !tokenValidated) return;
    setLoading(true);
    try {
      const url = filter === 'all'
        ? `${API}/admin/country-events`
        : `${API}/admin/country-events?status=${filter}`;
      const r = await axios.get(url, { headers: { 'X-Admin-Token': token } });
      setEvents(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken('');
        setTokenValidated(false);
      }
    } finally {
      setLoading(false);
    }
  }, [token, tokenValidated, filter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  async function approve(id) {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/country-events/${id}/approve`, {}, { headers: { 'X-Admin-Token': token } });
      await fetchEvents();
    } finally { setBusy(false); }
  }
  async function reject(id) {
    const reason = window.prompt('Raison du refus (optionnel) :', '') || '';
    setBusy(true);
    try {
      await axios.post(`${API}/admin/country-events/${id}/reject`, { reason }, { headers: { 'X-Admin-Token': token } });
      await fetchEvents();
    } finally { setBusy(false); }
  }
  async function remove(id) {
    if (!window.confirm('Supprimer définitivement cet évènement ?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/country-events/${id}`, { headers: { 'X-Admin-Token': token } });
      await fetchEvents();
    } finally { setBusy(false); }
  }
  async function feature(id) {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/country-events/${id}/feature`, {}, { headers: { 'X-Admin-Token': token } });
      await fetchEvents();
    } finally { setBusy(false); }
  }
  async function unfeature(id) {
    if (!window.confirm('Retirer la mise en avant de cet évènement ?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/country-events/${id}/unfeature`, {}, { headers: { 'X-Admin-Token': token } });
      await fetchEvents();
    } finally { setBusy(false); }
  }

  // Debounced search across the current tab's events.
  const { query, setQuery, debouncedQuery, filtered: filteredEvents } = useEventSearch(events, ADMIN_SEARCH_FIELDS);

  if (!token || !tokenValidated) {
    if (validating && token) {
      // Brief "Vérification…" screen while we validate stored token against
      // the server. Does NOT show any admin data or chrome.
      return (
        <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center font-['Manrope']">
          <div className="flex items-center gap-3 text-[#888]">
            <Loader2 className="w-5 h-5 animate-spin" />
            Vérification…
          </div>
        </div>
      );
    }
    return <AdminLogin onLogin={(t) => { setToken(t); setTokenValidated(true); }} />;
  }

  const counts = {
    pending: events.filter((e) => e.status === 'pending').length,
    approved: events.filter((e) => e.status === 'approved').length,
    rejected: events.filter((e) => e.status === 'rejected').length,
    cancelled: events.filter((e) => e.status === 'cancelled').length,
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope']">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/ball-trap-campagne" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="back-to-map">
              <ArrowLeft className="w-4 h-4" /> Carte
            </Link>
            <span className="text-white/20">·</span>
            <span className="text-sm text-[#FFC107] font-bold">Évènements</span>
            <Link to="/admin/users" className="text-sm text-white/70 hover:text-white" data-testid="goto-users">
              Utilisateurs
            </Link>
            <Link to="/admin/device-recovery" className="text-sm text-white/70 hover:text-white" data-testid="goto-device-recovery">
              Récup. device
            </Link>
          </div>
          <button
            onClick={() => { localStorage.removeItem(TOKEN_STORAGE_KEY); setToken(''); setTokenValidated(false); }}
            className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        <div className="flex items-baseline justify-between mb-6 gap-3 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-black">Modération · Ball-traps de campagne</h1>
          <Link to="/admin/country-events/new"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black text-sm font-bold hover:scale-105 transition-transform"
            data-testid="admin-new-event-btn">
            <Plus className="w-4 h-4" /> Nouveau ball-trap
          </Link>
        </div>

        {createdToast && (
          <div className="mb-5 p-3 rounded-xl bg-green-500/15 border border-green-500/30 text-green-200 text-sm flex items-center gap-2" data-testid="admin-created-toast">
            <Check className="w-4 h-4" /> Évènement publié avec succès.
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { key: 'pending', label: `En attente (${counts.pending})` },
            { key: 'approved', label: `Validés (${counts.approved})` },
            { key: 'rejected', label: `Refusés (${counts.rejected})` },
            { key: 'cancelled', label: `Annulés (${counts.cancelled})` },
            { key: 'all', label: 'Tous' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${filter === t.key ? 'bg-[#FFC107] text-black font-bold' : 'bg-white/5 text-[#C8C8C8] hover:bg-white/10'}`}
              data-testid={`admin-filter-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888] pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher (nom, ville, dép., email…)"
            className="w-full pl-10 pr-9 py-2.5 rounded-full bg-white/[0.04] border border-white/10 focus:border-[#FFC107]/50 outline-none text-sm placeholder:text-[#666]"
            data-testid="admin-events-search-input"
            aria-label="Rechercher dans les évènements de modération"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#888] hover:text-white hover:bg-white/10"
              aria-label="Effacer"
              data-testid="admin-events-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {debouncedQuery.trim() && (
            <p className="mt-1.5 ml-3 text-[11px] text-[#888]">
              {filteredEvents.length} résultat{filteredEvents.length > 1 ? 's' : ''} sur {events.length}
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#888]"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : events.length === 0 ? (
          <p className="text-[#888] italic text-sm" data-testid="admin-empty">Aucun évènement dans cette catégorie.</p>
        ) : filteredEvents.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center" data-testid="admin-empty-search">
            <p className="text-[#C8C8C8] mb-4">Aucun résultat pour « <span className="text-[#FFC107]">{debouncedQuery.trim()}</span> » dans cet onglet.</p>
            <button onClick={() => setQuery('')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold text-sm" data-testid="admin-events-reset-search">
              <X className="w-4 h-4" /> Effacer la recherche
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-events-list">
            {filteredEvents.map((evt) => (
              <EventRow key={evt.id} evt={evt} onApprove={approve} onReject={reject} onDelete={remove} onFeature={feature} onUnfeature={unfeature} busy={busy} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
